import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { parseISO, startOfDay, endOfDay } from 'date-fns';

interface SchoolHoliday {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  holiday_type: string;
}

export function useSchoolHolidays() {
  const { data: holidays = [], isLoading, error } = useQuery({
    queryKey: ['school-holidays'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('school_holidays')
        .select('*')
        .order('start_date');
      
      if (error) throw error;
      return data as SchoolHoliday[];
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnMount: 'always',
  });

  // Get school holiday (not israeli) for a date
  const getHolidayForDate = (date: Date): SchoolHoliday | null => {
    const checkDate = startOfDay(date);
    
    return holidays.find(holiday => {
      if (holiday.holiday_type === 'israeli') return false;
      const start = startOfDay(parseISO(holiday.start_date));
      const end = endOfDay(parseISO(holiday.end_date));
      return checkDate >= start && checkDate <= end;
    }) || null;
  };

  // Get Israeli holiday for a date
  const getIsraeliHolidayForDate = (date: Date): SchoolHoliday | null => {
    const checkDate = startOfDay(date);
    
    return holidays.find(holiday => {
      if (holiday.holiday_type !== 'israeli') return false;
      const start = startOfDay(parseISO(holiday.start_date));
      const end = endOfDay(parseISO(holiday.end_date));
      return checkDate >= start && checkDate <= end;
    }) || null;
  };

  const isSchoolHoliday = (date: Date): boolean => {
    return getHolidayForDate(date) !== null;
  };

  const isIsraeliHoliday = (date: Date): boolean => {
    return getIsraeliHolidayForDate(date) !== null;
  };

  return { 
    holidays, 
    getHolidayForDate, 
    getIsraeliHolidayForDate,
    isSchoolHoliday, 
    isIsraeliHoliday,
    isLoading, 
    error 
  };
}
